import React, { useState } from "react";
import { Button, Form, Input, Modal } from "antd";
import { CustomIcons } from "@/assets/index";
import { modelTitle } from "@/styles/ModalStyles";

const { TextArea } = Input;

interface DeactivateResponderAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeactivate: (reason: string) => void;
  isLoading?: boolean;
  adminName?: string;
}

const DeactivateResponderAdminModal: React.FC<
  DeactivateResponderAdminModalProps
> = ({
  isOpen,
  onClose,
  onDeactivate,
  isLoading = false,
  adminName = "Responder Admin",
}) => {
  const [form] = Form.useForm();
  const [reason, setReason] = useState("");
  const maxLength = 500;

  const handleClose = () => {
    form.resetFields();
    setReason("");
    onClose();
  };

  const handleDeactivate = async () => {
    try {
      await form.validateFields();
      onDeactivate(reason);
      handleClose();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setReason(value);
      form.setFieldsValue({ reason: value });
    }
  };

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={handleClose}
      width={600}
      closeIcon={
        <CustomIcons.CloseIcon
          width={30}
          height={30}
          viewBox={null}
          svgColor="#1C274C"
        />
      }
      footer={null}
    >
      <div className="flex w-full py-3 justify-center" style={modelTitle}>
        Deactivate Responder Admin
      </div>

      <div className="mb-6 text-gray-600 text-center">
        <p className="text-lg mb-2">
          Are you sure you want to deactivate this {adminName}?
        </p>
        <p className="text-sm">
          This action will revoke their access to the Responder Company.
        </p>
      </div>

      <Form
        form={form}
        name="deactivateResponderAdmin"
        onFinish={handleDeactivate}
        layout="vertical"
        className="w-full"
      >
        <Form.Item
          name="reason"
          rules={[
            {
              required: true,
              message: "Please enter a reason for deactivation!",
            },
            {
              min: 10,
              message: "Reason must be at least 10 characters long!",
            },
          ]}
        >
          <div className="relative">
            <TextArea
              placeholder="Enter reason for deactivation"
              value={reason}
              onChange={handleReasonChange}
              rows={6}
              className="resize-none"
              style={{
                borderRadius: "12px",
                padding: "16px",
                fontSize: "16px",
              }}
            />
            <div className="absolute bottom-3 right-3 text-gray-400 text-sm">
              /{maxLength}
            </div>
          </div>
        </Form.Item>

        {/* Footer Buttons */}
        <div className="flex w-full gap-4 justify-center pt-4">
          <Button
            type="default"
            size="large"
            className="px-8 py-2 h-12 rounded-full border-2 border-blue-500 text-blue-500 hover:border-blue-600 hover:text-blue-600"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            className="px-8 py-2 h-12 rounded-full bg-red-500 hover:bg-red-600 border-red-500 hover:border-red-600"
            loading={isLoading}
            onClick={handleDeactivate}
          >
            Deactivate
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default DeactivateResponderAdminModal;
